"use client";

import ProductCard from "@/components/product/ProductCard";
import SectionHeader from "@/components/motion/SectionHeader";
import type { LatestDrop as LatestDropData } from "@/lib/validations/cms";
import type { Product } from "@/types/product";
import type { Drop } from "@/types/drop";
import EditableText from "@/components/edit-mode/EditableText";
import { getDropStatus } from "@/lib/commerce/availability";

export default function LatestDropClient({
  data,
  dropData,
}: {
  data: LatestDropData;
  dropData: { drop: Drop; products: Product[] } | null;
}) {
  if (!dropData) {
    return (
      <section className="bg-background py-8 md:py-16">
        <div className="mx-auto max-w-[1920px] px-4 sm:px-6 lg:px-8">
          <SectionHeader
            title={<EditableText section="latestDrop" field="title" value={data.title} />}
            subtitle={data.subtitle}
          />
          <p className="text-muted-foreground col-span-full py-12 text-center">
            <EditableText section="latestDrop" field="emptyState" value={data.emptyState} />
          </p>
        </div>
      </section>
    );
  }

  const { drop, products } = dropData;
  const isEnded = getDropStatus(drop) === "ended";

  return (
    <section className="bg-background py-8 md:py-16">
      <div className="mx-auto max-w-[1920px] px-4 sm:px-6 lg:px-8">
        <SectionHeader
          title={
            <EditableText
              section="latestDrop"
              field="title"
              value={data.titleOverride ? data.title : drop.name}
            />
          }
          subtitle={<EditableText section="latestDrop" field="subtitle" value={data.subtitle} />}
          linkText={<EditableText section="latestDrop" field="linkText" value={data.linkText} />}
          href={`/drops/${drop.id}`}
        />

        {isEnded && (
          <div className="border-foreground/10 bg-muted/30 mb-8 border p-6 text-center">
            <p className="text-muted-foreground text-sm">
              This drop has ended. Browse the archive below.
            </p>
          </div>
        )}

        {products.length > 0 ? (
          <div className="grid grid-cols-2 gap-x-4 gap-y-8 md:gap-x-6 md:gap-y-12 lg:grid-cols-4">
            {products.map((product, index) => (
              <ProductCard
                key={product.id}
                name={product.name}
                price={product.price || 0}
                images={product.images}
                id={product.id}
                sizes={product.sizes}
                stock={product.stock}
                is_preorder={product.is_preorder}
                index={index}
                archiveMode={isEnded}
              />
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground col-span-full py-12 text-center">
            {data.productsEmptyState ? data.productsEmptyState : "No products in this drop."}
          </p>
        )}
      </div>
    </section>
  );
}
